import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  MoreVert,
  PriorityHigh,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import moment from 'moment';
import BillingHistory, { Transaction } from '../BillingHistory';
import { FREE_TIER_EXPIRY } from '../../lib/Auth/Auth';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  maxSchedules: number;
  maxStaff: number;
  planId: string | null;
  badge?: string;
  highlighted?: boolean;
}

interface BillingTabProps {
  currentPlan: Record<string, any>;
  paymentMethod: Record<string, any> | null;
  subscription: Record<string, any>;
  transactions: Transaction[];
  transactionsToShow: number;
  paymentMethodLoading: boolean;
  transactionsLoading: boolean;
  profileData: Record<string, any>;
  selectedNewPlan: string | null;
  showUpgradePlan: boolean;
  plans: Plan[];
  onUpgradePlanClick: () => void;
  onPaymentMethodClick: () => void;
  setShowPlanSwitchModal: (value: boolean) => void;
  setSelectedNewPlan: (value: string | null) => void;
  onTransactionsShowChange: (value: number) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export default function BillingTab({
  currentPlan,
  paymentMethod,
  subscription,
  transactions,
  transactionsToShow,
  paymentMethodLoading,
  transactionsLoading,
  profileData,
  selectedNewPlan,
  showUpgradePlan,
  plans,
  onUpgradePlanClick,
  onPaymentMethodClick,
  setShowPlanSwitchModal,
  setSelectedNewPlan,
  onTransactionsShowChange,
  onMenuClick,
}: BillingTabProps) {
  const getCurrentPlanData = () => plans.find(p => p.planId === currentPlan.planId);

  if (showUpgradePlan) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={onUpgradePlanClick}
              sx={{ color: 'primary.main' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Choose Your Plan
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the plan that best fits your business needs
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={3}>
          {plans.map((plan) => {
            if (plan.id === 'free') {
              return null;
            }
            const isCurrentPlan = plan.planId === currentPlan.planId;

            return (
              <Grid item xs={12} md={4} key={plan.id} onClick={() => {
                if (!isCurrentPlan) {
                  setSelectedNewPlan(plan.planId);
                  setShowPlanSwitchModal(true);
                }
              }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: 2,
                    borderColor: selectedNewPlan === plan.planId ? 'primary.main' : isCurrentPlan ? 'info.main' : 'divider',
                    bgcolor: selectedNewPlan === plan.planId ? 'primary.50' : isCurrentPlan ? 'info.50' : 'background.paper',
                    transition: 'all 0.3s ease',
                    cursor: isCurrentPlan ? 'default' : 'pointer',
                    '&:hover': isCurrentPlan ? {} : {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    },
                    overflow: 'visible'
                  }}
                >
                  {/* Badge */}
                  {(plan.badge || isCurrentPlan) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1,
                      }}
                    >
                      <Chip
                        label={isCurrentPlan ? 'Current Plan' : plan.badge}
                        size="small"
                        color={isCurrentPlan ? 'info' : 'primary'}
                        sx={{
                          fontWeight: 'bold',
                          color: 'white'
                        }}
                      />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: plan.badge || isCurrentPlan ? 3 : 2 }}>
                    {/* Plan Header */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="text.primary"
                        gutterBottom
                      >
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {plan.description}
                      </Typography>

                      {/* Price */}
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          color={selectedNewPlan === plan.planId ? 'primary' : 'text.primary'}
                        >
                          ${plan.price}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ ml: 0.5 }}>
                          /month
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Key Stats */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={selectedNewPlan === plan.planId ? 'primary' : 'text.primary'}
                          >
                            {plan.maxSchedules === -1 ? '∞' : plan.maxSchedules}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Schedules
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={selectedNewPlan === plan.planId ? 'primary' : 'text.primary'}
                          >
                            {plan.maxStaff === -1 ? '∞' : plan.maxStaff}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Staff Members
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Features List */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                        Features included:
                      </Typography>
                      <List dense sx={{ py: 0 }}>
                        {plan.features.map((feature, idx) => (
                          <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckCircleIcon
                                fontSize="small"
                                color={selectedNewPlan === plan.planId ? 'primary' : isCurrentPlan ? 'success' : 'action'}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.primary'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Comparison Note */}
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Need help choosing?
          </Typography>
          <Typography variant="body2">
            All plans include core scheduling features. Upgrade for more schedules, staff capacity, and advanced features like analytics and priority support.
          </Typography>
          <Typography variant="body2">
            <br />
            Contact our support team for personalized recommendations based on your business needs.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
        Billing & Subscription
      </Typography>
      {paymentMethodLoading && <LinearProgress color="info" />}

      <Grid container spacing={3}>
        {/* Current Plan Card */}
        {!paymentMethodLoading && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'background.default', border: 2, borderColor: 'primary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" fontWeight={800}>
                      Current Plan
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {getCurrentPlanData()?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {getCurrentPlanData()?.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={paymentMethod?.paidThroughDate && subscription.id == null ? 'Expiring' : currentPlan?.id !== 'free' ? 'Active' : 'Free'}
                      color={paymentMethod?.paidThroughDate && subscription.id == null ? 'info' : currentPlan?.id !== 'free' ? 'success' : 'secondary'}
                      sx={{ color: 'white' }}
                      icon={paymentMethod?.paidThroughDate && subscription.id == null ? <PriorityHigh /> : currentPlan?.id !== 'free' ? <CheckCircleIcon /> : <CancelIcon />}
                    />
                    <IconButton
                      disabled={currentPlan?.id === 'free' || (paymentMethod?.paidThroughDate && subscription.id == null)}
                      size="small"
                      onClick={onMenuClick}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Monthly Cost
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      ${getCurrentPlanData()?.price}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Schedules
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {moment(profileData.created).isAfter(moment().subtract(FREE_TIER_EXPIRY, 'days')) ? '0' : getCurrentPlanData()?.maxSchedules === -1 ? '∞' : getCurrentPlanData()?.maxSchedules}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Staff Limit
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {moment(profileData.created).isAfter(moment().subtract(FREE_TIER_EXPIRY, 'days')) ? '0' : getCurrentPlanData()?.maxStaff === -1 ? '∞' : getCurrentPlanData()?.maxStaff}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {moment(profileData.created).isAfter(moment().subtract(FREE_TIER_EXPIRY, 'days')) ? 'Free Tier Expires' : 'Next Billing Date'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {moment(profileData.created).isAfter(moment().subtract(FREE_TIER_EXPIRY, 'days')) ? "Never!" : moment(subscription.nextBillingDate).format('MMM D')}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Button
                  color="info"
                  variant="contained"
                  sx={{ float: 'right', color: 'white' }}
                  onClick={onUpgradePlanClick}
                >
                  {moment(profileData.created).isAfter(moment().subtract(FREE_TIER_EXPIRY, 'days')) ? 'Select your Plan' : (paymentMethod?.paidThroughDate && subscription.id == null ? 'Resume Plan' : 'Upgrade Plan')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {/* Payment Method */}
        {!paymentMethodLoading && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'background.default', border: 2, borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary" fontWeight={800}>
                  Payment Method
                </Typography>
                {paymentMethod && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Credit Card: {paymentMethod.cardType} {paymentMethod.last4}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Expires: {paymentMethod.expirationYear} / {paymentMethod.expirationMonth}
                    </Typography>
                  </>
                )}
                {!paymentMethod && (
                  <Alert severity="warning">No payment method on file.</Alert>
                )}
              </CardContent>
              <CardActions>
                <Button
                  color="info"
                  variant="text"
                  sx={{ float: 'right' }}
                  onClick={onPaymentMethodClick}
                >
                  {paymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {/* Billing History */}
        <Grid item xs={12}>
          <Divider variant="middle" sx={{ my: 2 }} />

          {/* Header with Select */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Billing History
            </Typography>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="transaction-limit-label">Show Transactions</InputLabel>
              <Select
                labelId="transaction-limit-label"
                value={transactionsToShow}
                label="Show Transactions"
                onChange={(e) => onTransactionsShowChange(Number(e.target.value))}
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <MenuItem value={10}>Last 10 Transactions</MenuItem>
                <MenuItem value={25}>Last 25 Transactions</MenuItem>
                <MenuItem value={50}>Last 50 Transactions</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {transactionsLoading ? (
            <LinearProgress color="info" />
          ) : (
            <BillingHistory  transactions={transactions || []} userId={profileData.userId} />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
}
